import { useState, type FormEvent, type ReactNode } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useRessource } from '../hooks/useRessource';
import { useAnnee } from '../hooks/useAnnee';
import { ErreurApi, api } from '../lib/apiClient';
import { AideEnBulle, Badge, Champ, Chargement, EnTetePage, Erreur, Infobulle, Modale, Onglets } from '../components/Elements';
import { PiedFormulaire } from './ReferentielsPages';
import { exporterTableau, montantExport } from '../lib/export';
import {
  formaterHeure,
  formaterMois,
  formaterMontant,
  libelleJour,
} from '../lib/format';
import { libelleStatutContrat, libelleTempsTravail, natureRemuneration } from '../lib/contrat';
import { IconeMutuelle, IconeTransport } from '../components/IconesContrat';
import {
  HeuresMinutes,
  LegendeNature,
  MontantNature,
  ResumePaie,
  TableauElementsPaie,
} from '../components/ElementsPaie';
import { EnvoiFichesPaie, IconeEnveloppe } from '../components/EnvoiFichesPaie';
import { heuresMinutesTexte, moisPortes as moisPortesDe } from '../lib/paie';
import type { BaseCalculee, BilanCloture, CalculPaie, PaiePersonnel } from '../types';
import { BoutonExcel } from '../components/BoutonsIcone';

/**
 * Paie de l'entite active (menu Equipe -> Paie, lot P3).
 *
 * L'ecran reproduit le tableau du classeur des salaires : une ligne par
 * personnel, un net par mois, les totaux en pied. Tout est CALCULE par le
 * serveur depuis les elements de paie — le clic sur une ligne les montre.
 *
 * Donnees de paie : `contrat.lire` (Direction), consultations
 * journalisees cote serveur (CDC Paie §4), cloisonnees par RLS.
 */

export function PaiePage() {
  const { peut, entiteActive } = useAuth();
  const cle = entiteActive?.id ?? '';
  const annee = useAnnee();
  const [detail, setDetail] = useState<string | null>(null);
  // Ouvert depuis la SYNTHESE d'un mois : le detail s'ouvre sur ce mois et
  // ne propose pas d'ajouter un element — on consulte, on ne saisit pas.
  const [detailMois, setDetailMois] = useState<string | null>(null);
  const [onglet, setOnglet] = useState('general');
  // La SYNTHESE MENSUELLE n'est plus un onglet (2026-10-02) : on y entre
  // par le MOIS, en cliquant son en-tete dans le tableau du regime
  // general. `null` : le tableau annuel ; sinon le mois regarde.
  const [moisSynthese, setMoisSynthese] = useState<string | null>(null);

  const paie = useRessource(
    () =>
      annee.anneeId
        ? api.get<CalculPaie>('/api/paie', { anneeId: annee.anneeId })
        : Promise.resolve(null),
    [annee.anneeId, cle],
  );

  const donnees = paie.donnees ?? null;
  const personnelDetail = donnees?.personnel.find((s) => s.contrat_id === detail) ?? null;

  return (
    <>
      <EnTetePage
        titre={
          <>
            Paie
            <AideEnBulle texte="Les mois ouverts sont calculés depuis les inscriptions et les créneaux ; un mois clôturé est enregistré tel qu’il a été déclaré au CEA. Cliquez un salarié pour voir le détail, un mois pour sa synthèse et sa clôture." />
          </>
        }
      />

      <Onglets
        onglets={[
          { cle: 'general', libelle: 'Régime général' },
          { cle: 'cachet', libelle: 'Cachet' },
        ]}
        actif={onglet}
        onChanger={(cle) => {
          setOnglet(cle);
          setMoisSynthese(null);
        }}
      />

      {paie.erreur && <Erreur message={paie.erreur} onReessayer={paie.recharger} />}
      {paie.chargement || !donnees ? (
        <Chargement />
      ) : onglet === 'general' && moisSynthese ? (
        <SyntheseMensuelle
          // Recree a chaque mois d'entree : son selecteur repart du bon mois.
          key={moisSynthese}
          donnees={donnees}
          moisInitial={moisSynthese}
          onRetour={() => setMoisSynthese(null)}
          onDetail={(contratId, mois) => {
            setDetailMois(mois);
            setDetail(contratId);
          }}
          peutGerer={peut('contrat.gerer') && annee.modifiable}
          peutCorriger={peut('paie.corriger') && annee.modifiable}
          // Pas de condition sur l'annee : envoyer un bulletin n'ecrit rien
          // dans les montants, et une annee close en garde le droit.
          peutEnvoyerFiches={peut('paie.envoyer_fiche')}
          onChange={paie.recharger}
        />
      ) : (
        <TableauAnnuel
          donnees={donnees}
          mode={onglet === 'cachet' ? 'cachet' : 'general'}
          onDetail={(contratId) => {
            setDetailMois(null);
            setDetail(contratId);
          }}
          peutGerer={peut('contrat.gerer') && annee.modifiable}
          onChange={paie.recharger}
          // La synthese est celle du regime general : le cachet n'y mene pas.
          onMois={onglet === 'general' ? setMoisSynthese : undefined}
        />
      )}

      {personnelDetail && donnees && (
        <DetailPersonnel
          personnel={personnelDetail}
          mode={onglet === 'cachet' ? 'cachet' : 'general'}
          moisInitial={detailMois}
          sansAjout={detailMois !== null}
          anneeId={donnees.annee_id}
          mois={donnees.mois}
          moisClotures={donnees.mois_clotures}
          peutGerer={peut('contrat.gerer') && annee.modifiable}
          peutCorriger={peut('paie.corriger') && annee.modifiable}
          onFermer={() => setDetail(null)}
          onChange={paie.recharger}
        />
      )}
    </>
  );
}

/**
 * Tableau annuel d'un REGIME : 12 mois x personnel. Chaque element de
 * paie releve du regime general ou du cachet selon le mode de
 * remuneration de l'ACTIVITE de son creneau — `intermittent` seul part
 * au cachet, tout le reste (permanent, non renseigne, sans creneau)
 * releve du regime general. General + cachet = net total, au centime.
 */
/**
 * EN-TETE FIGE des deux tableaux de paie (2026-10-02), sur la recette du
 * composant `Tableau` : chaque cellule d'en-tete colle sous le bandeau
 * (`--hauteur-bandeau`), avec un fond — sans lui les lignes defilent a
 * travers — et son liseré en ombre interieure, une bordure de tableau
 * restant en arriere d'une cellule figee.
 *
 * Le conteneur ne garde `overflow-x-auto` que sous `lg` : poser
 * `overflow-x` cree un conteneur de defilement sur les DEUX axes, et
 * l'en-tete s'accrocherait a lui au lieu de la page.
 */
const ENTETE_FIGEE =
  "sticky z-10 bg-encre-50 shadow-[inset_0_-1px_0_#c5c7cc] after:pointer-events-none after:absolute after:inset-x-0 after:top-full after:h-2 after:bg-gradient-to-b after:from-black/10 after:to-transparent after:content-['']";
const HAUT_FIGE = { top: 'var(--hauteur-bandeau, 63px)' } as const;

/** Les icones mutuelle et transport d'un salarie, apres son nom. */
function IconesAvantages({ personnel }: { personnel: PaiePersonnel }) {
  const transport = personnel.titre_transport ?? 0;
  if (!personnel.mutuelle && transport <= 0) return null;
  return (
    <span className="ml-2 inline-flex items-center gap-1 align-middle">
      {personnel.mutuelle && <IconeMutuelle depuis={personnel.date_souscription_mutuelle} />}
      {transport > 0 && <IconeTransport montant={transport} type={personnel.titre_transport_type} />}
    </span>
  );
}

function TableauAnnuel({
  donnees,
  mode,
  onDetail,
  onMois,
  peutGerer,
  onChange,
}: {
  donnees: CalculPaie;
  mode: 'general' | 'cachet';
  onDetail: (contratId: string) => void;
  /** Present, l'en-tete de chaque mois devient un lien vers sa synthese. */
  onMois?: (mois: string) => void;
  peutGerer: boolean;
  onChange: () => void;
}) {
  const [cloture, setCloture] = useState(false);
  const nets = (s: PaiePersonnel) => (mode === 'cachet' ? s.nets_cachet : s.nets_general);
  const annuel = (s: PaiePersonnel) =>
    mode === 'cachet' ? s.net_annuel_cachet : s.net_annuel_general;
  const lignes = donnees.personnel.filter(
    (s) => annuel(s) !== 0 || nets(s).some((n) => n !== 0),
  );
  const totauxMensuels =
    mode === 'cachet' ? donnees.totaux.nets_cachet : donnees.totaux.nets_general;
  const totalAnnuel =
    mode === 'cachet' ? donnees.totaux.net_annuel_cachet : donnees.totaux.net_annuel_general;

  const libelleMois = (iso: string) => formaterMois(iso, { court: true, avecAnnee: false });

  // CLOTURER DEPUIS LA VUE ANNUELLE (2026-10-02) : la cloture est
  // sequentielle, c'est donc toujours le PREMIER MOIS OUVERT. Le bouton
  // ne le nomme pas ; la fenetre de confirmation, si (« Cloturer la paie
  // de septembre 2026 ») — on ne cloture pas a l'aveugle.
  const premierOuvert = donnees.mois.find((m) => !donnees.mois_clotures.includes(m)) ?? null;
  const indexOuvert = premierOuvert ? donnees.mois.indexOf(premierOuvert) : -1;

  const exporter = () =>
    exporterTableau(
      `paie-${mode === 'cachet' ? 'cachet' : 'regime-general'}-${donnees.annee_libelle}`,
      ['Personnel', ...donnees.mois.map((m) => formaterMois(m)), 'Total'],
      [
        ...lignes.map((s) => [
          `${s.prenom_usuel} ${s.nom ?? ''}`.trim(),
          ...nets(s).map((n) => montantExport(n)),
          montantExport(annuel(s)),
        ]),
        ['Salaires nets', ...totauxMensuels.map((n) => montantExport(n)), montantExport(totalAnnuel)],
      ],
    );

  /**
   * Le mois se lit a sa couleur, et la couleur encode L'ETAT, plus le
   * temps (2026-09-08) : un mois CLOTURE est en noir avec un cadenas —
   * enregistre, declare, intouchable ; un mois OUVERT est en gris —
   * calcule a chaque lecture depuis les inscriptions. La date du jour ne
   * decide rien : un septembre non cloture reste gris en decembre.
   */
  const clotures = new Set(donnees.mois_clotures);
  const tonDuMois = (m: string): string =>
    clotures.has(m) ? 'text-encre-800' : 'text-encre-400';

  if (lignes.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-encre-200 px-4 py-3 text-sm text-encre-500">
        {mode === 'cachet'
          ? 'Aucun élément de paie au cachet sur cette année. Un élément y apparaît quand son créneau porte une activité au mode de rémunération « Intermittent (Cachet) ».'
          : 'Aucun élément de paie au régime général sur cette année.'}
      </p>
    );
  }

  return (
    <>
      <div className="mb-3 flex flex-wrap items-center justify-end gap-2">
        {mode === 'general' && peutGerer && premierOuvert && (
          <button type="button" className="bouton-principal" onClick={() => setCloture(true)}>
            Clôturer la période
          </button>
        )}
        <BoutonExcel libelle="Exporter la paie (Excel)" onClick={() => void exporter()} />
      </div>

      {cloture && premierOuvert && (
        <ModaleCloture
          anneeId={donnees.annee_id}
          mois={premierOuvert}
          salaries={donnees.personnel.filter((p) => p.nets_general[indexOuvert] !== 0).length}
          total={donnees.totaux.nets_general[indexOuvert] ?? 0}
          ecartes={donnees.creneaux_ecartes}
          onFermer={() => setCloture(false)}
          onCloture={() => {
            setCloture(false);
            onChange();
          }}
        />
      )}

    <div className="carte overflow-x-auto lg:overflow-x-visible">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-encre-500">
            {/* Fige en haut ET a gauche : au-dessus des autres en-tetes. */}
            <th className={`${ENTETE_FIGEE} left-0 z-20 px-3 py-2`} style={HAUT_FIGE}>
              Salarié
            </th>
            {donnees.mois.map((m) => (
              <th
                key={m}
                className={`${ENTETE_FIGEE} whitespace-nowrap px-2 py-2 text-right font-medium ${tonDuMois(m)}`}
                style={HAUT_FIGE}
              >
                {clotures.has(m) && (
                  <Infobulle texte="Période clôturée" className="inline-flex">
                    <Cadenas />
                  </Infobulle>
                )}
                {onMois ? (
                  // Un bouton, pour le clavier ; souligne comme un lien, la
                  // couleur du mois (noir cloture, gris ouvert) conservee.
                  <Infobulle texte={`Synthèse de ${formaterMois(m)}`}>
                    <button
                      type="button"
                      className="font-medium uppercase tracking-wide underline decoration-dotted underline-offset-2 hover:decoration-solid"
                      onClick={() => onMois(m)}
                    >
                      {libelleMois(m)}
                    </button>
                  </Infobulle>
                ) : (
                  libelleMois(m)
                )}
              </th>
            ))}
            <th className={`${ENTETE_FIGEE} px-3 py-2 text-right`} style={HAUT_FIGE}>
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          {lignes.map((s) => (
            <tr
              key={s.contrat_id}
              className="cursor-pointer border-b border-encre-100 hover:bg-encre-50"
              onClick={() => onDetail(s.contrat_id)}
            >
              <td className="sticky left-0 whitespace-nowrap bg-white px-3 py-1.5 font-medium">
                {s.prenom_usuel} <span className="uppercase text-encre-500">{s.nom ?? ''}</span>
                {/* Mutuelle et transport en icones, comme sur la liste des
                    contrats — au regime general seulement, le cachet ne
                    portant ni l'un ni l'autre. */}
                {mode === 'general' && <IconesAvantages personnel={s} />}
              </td>
              {nets(s).map((montant, i) => (
                <td
                  key={i}
                  className={`tabulaire px-2 py-1.5 text-right ${tonDuMois(donnees.mois[i] ?? '')}`}
                >
                  {montant === 0 ? (
                    <span className="text-encre-300">—</span>
                  ) : (
                    // Le salaire fixe d'un administratif saisi en brut est
                    // souligne (2026-10-16) ; la legende sous le tableau dit
                    // la regle.
                    <MontantNature
                      nature={s.statut === 'administratif' && mode === 'general' ? s.natures_salaire[i] : null}
                    >
                      {(montant / 100).toLocaleString('fr-FR', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </MontantNature>
                  )}
                </td>
              ))}
              <td className="tabulaire px-3 py-1.5 text-right font-medium">
                {formaterMontant(annuel(s))}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot className="text-sm">
          <tr className="border-t-2 border-encre-300 font-medium">
            <td className="sticky left-0 bg-white px-3 py-2">Salaires nets</td>
            {totauxMensuels.map((montant, i) => (
              <td key={i} className="tabulaire px-2 py-2 text-right">
                {(montant / 100).toLocaleString('fr-FR', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </td>
            ))}
            <td className="tabulaire px-3 py-2 text-right">{formaterMontant(totalAnnuel)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
    {mode === 'general' && <LegendeNature classe="mt-2" />}
    </>
  );
}

/**
 * Un montant de la synthese qui a quelque chose a dire : souligne en
 * pointille, l'explication dans l'infobulle commune (2026-11-04). Le
 * salaire net de mutuelle et le transport sans justificatif s'en servent.
 */
function MontantExplique({
  texte,
  accessible,
  children,
}: {
  texte: string;
  /** Ce que lit un lecteur d'ecran ; le texte de la bulle a defaut. */
  accessible?: string;
  children: ReactNode;
}) {
  return (
    <Infobulle texte={texte} className="inline-block cursor-help border-b border-dotted border-encre-300">
      <span tabIndex={0} aria-label={accessible ?? texte}>
        {children}
      </span>
    </Infobulle>
  );
}

/**
 * Synthese d'un mois de paie (P4) — le support de la declaration CEA.
 *
 * C'est la feuille « Synthese salaire » du classeur, mais CALCULEE : la
 * feuille etait une recopie manuelle, et elle avait derive de la feuille
 * Salaires sur quatre lignes (bases ajoutees apres la declaration,
 * deduction GUSO exclue a la main). Ici les colonnes sortent du moteur —
 * net = salaire + prime + transport - mutuelle, toujours a jour des
 * elements de paie.
 */
function SyntheseMensuelle({
  donnees,
  moisInitial,
  onRetour,
  onDetail,
  peutGerer,
  peutCorriger,
  peutEnvoyerFiches,
  onChange,
}: {
  donnees: CalculPaie;
  /** Le mois par lequel on est entre, depuis le tableau annuel. */
  moisInitial: string;
  onRetour: () => void;
  /** Ouvre le detail des elements de paie du salarie, sur le mois affiche. */
  onDetail: (contratId: string, mois: string) => void;
  peutGerer: boolean;
  peutCorriger: boolean;
  peutEnvoyerFiches: boolean;
  onChange: () => void;
}) {
  // Le mois affiche est celui par lequel on est ENTRE (2026-10-02).
  const clotures = new Set(donnees.mois_clotures);
  const premierOuvert = donnees.mois.find((m) => !clotures.has(m)) ?? null;
  const dernierClos = donnees.mois_clotures[donnees.mois_clotures.length - 1] ?? null;
  const moisChoisi = moisInitial;
  const [cloture, setCloture] = useState(false);
  const [reouverture, setReouverture] = useState<string | null>(null);
  // L'envoi des fiches de paie (2026-11-04) : `null` fermee ; ouverte, sur
  // un salarie ou sur tous. Seulement sur un mois clos : le bulletin n'existe
  // qu'apres la declaration.
  const [envoiFiches, setEnvoiFiches] = useState<{ contratId: string | null } | null>(null);
  const index = Math.max(0, donnees.mois.indexOf(moisChoisi));
  const estCloture = clotures.has(moisChoisi);
  const envoiPossible = peutEnvoyerFiches && estCloture;

  const rouvrir = async () => {
    if (
      !window.confirm(
        `Rouvrir la paie de ${formaterMois(moisChoisi)} ? Ce qui a été déclaré sera effacé et le mois redeviendra calculé.`,
      )
    )
      return;
    setReouverture(null);
    try {
      await api.delete(`/api/paie/periodes/${moisChoisi}?anneeId=${donnees.annee_id}`);
      onChange();
    } catch (cause) {
      setReouverture(cause instanceof ErreurApi ? cause.message : 'Réouverture impossible.');
    }
  };

  const variables = donnees.personnel.filter((s) => s.bases.length > 0 || s.net_annuel !== 0);

  // Le SALAIRE affiche est net de mutuelle : la cotisation est deduite de
  // la colonne plutot qu'affichee a part — le total, lui, ne change pas
  // (salaire - mutuelle + prime + transport = le meme net qu'avant).
  // La colonne ETP (0.72.0) a cede sa place a la BASE FORFAITAIRE
  // (2026-11-02, demande de Georges) : l'eligibilite du contrat, lue
  // telle quelle, Oui ou Non.
  const lignes = variables.map((s) => ({
    personnel: s,
    detail: s.details_mensuels[index]!,
    salaireNet: s.details_mensuels[index]!.salaire - s.details_mensuels[index]!.mutuelle,
    net: s.nets_general[index]!,
  }));
  // Les totaux viennent du MOTEUR, accumules en flottants : sommer les
  // cellules deja arrondies decalerait d'un centime par rapport au
  // tableau annuel — les deux onglets doivent dire le meme chiffre.
  const detailTotal = donnees.totaux.details_mensuels[index]!;
  const totaux = {
    heures: detailTotal.heures,
    salaireNet: detailTotal.salaire - detailTotal.mutuelle,
    prime: detailTotal.prime,
    transport: detailTotal.transport,
    net: donnees.totaux.nets_general[index]!,
  };

  const baseForfaitaire = (valeur: boolean) =>
    valeur ? 'Oui' : <span className="text-encre-400">Non</span>;

  // Les heures de la SYNTHESE s'arrondissent aux CINQ MINUTES INFERIEURES
  // avant le passage en HH:MM : elles se DEDUISENT du salaire du mois,
  // divise par le taux horaire moyen du contrat — la minute pres n'y a
  // donc pas de sens de paie. Cinq minutes et vers le bas, et non le
  // quart d'heure le plus proche (decisions de Georges du 2026-11-02) :
  // un temps plein a 35 h fait 151,67 h, soit 151 h 40, la duree legale
  // — le quart d'heure le plus proche affichait 151 h 45, au-dessus
  // d'elle, le quart inferieur 151 h 30, en dessous ; les cinq minutes
  // inferieures donnent 151 h 40 exactement, et aucune duree affichee ne
  // depasse sa valeur reelle. La valeur decimale survit en infobulle, et
  // c'est bien elle qui, divisee dans le salaire, redonne le taux moyen :
  // par construction, cette fois. L'epsilon absorbe 9,75 lu 9,7499999.
  const auxCinqMinutes = (valeur: number) => Math.floor(valeur * 12 + 1e-9) / 12;
  const heures = (valeur: number) =>
    valeur === 0 ? (
      <span className="text-encre-300">—</span>
    ) : (
      <Infobulle texte={`${valeur.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} h`}>
        <HeuresMinutes valeur={auxCinqMinutes(valeur)} />
      </Infobulle>
    );

  const libelleMoisLong = (iso: string) => formaterMois(iso);

  const exporter = () => {
    exporterTableau(
      `synthese-paie-${moisChoisi.slice(0, 7)}`,
      ['Personnel', 'Mutuelle', 'Duree', 'Base forfaitaire', 'Salaire', 'Prime anciennete', 'Transport', 'Total'],
      [
        ...lignes.map((l) => [
          `${l.personnel.prenom_usuel} ${l.personnel.nom ?? ''}`.trim(),
          l.personnel.mutuelle ? 'oui' : '',
          l.detail.heures === 0 ? '' : heuresMinutesTexte(auxCinqMinutes(l.detail.heures)),
          l.personnel.base_forfaitaire ? 'oui' : 'non',
          montantExport(l.salaireNet),
          montantExport(l.detail.prime),
          montantExport(l.detail.transport),
          montantExport(l.net),
        ]),
        [
          'Total',
          '',
          heuresMinutesTexte(auxCinqMinutes(totaux.heures)),
          '',
          montantExport(totaux.salaireNet),
          montantExport(totaux.prime),
          montantExport(totaux.transport),
          montantExport(totaux.net),
        ],
      ],
    );
  };

  const montant = (centimes: number, neutre = false) =>
    centimes === 0 && neutre ? (
      <span className="text-encre-300">—</span>
    ) : (
      formaterMontant(centimes)
    );

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <button type="button" className="text-sm text-encre-600 underline" onClick={onRetour}>
          ← Tableau annuel
        </button>
        {/* La periode est une INFORMATION, plus un selecteur (2026-10-02) :
            on change de mois en revenant au tableau annuel. */}
        <span className="text-sm text-encre-600">
          Période de paie :{' '}
          <span className="font-medium text-encre-900">{libelleMoisLong(moisChoisi)}</span>
        </span>
        {estCloture && (
          <Badge ton="neutre">
            <Cadenas /> clôturée
          </Badge>
        )}
        <span className="ml-auto flex items-center gap-2">
          {envoiPossible && (
            <button type="button" className="bouton-secondaire" onClick={() => setEnvoiFiches({ contratId: null })}>
              Envoyer les fiches de paie
            </button>
          )}
          {peutCorriger && estCloture && moisChoisi === dernierClos && (
            <button type="button" className="bouton-secondaire" onClick={() => void rouvrir()}>
              Rouvrir la période
            </button>
          )}
          {peutGerer && !estCloture && moisChoisi === premierOuvert && (
            <button type="button" className="bouton-principal" onClick={() => setCloture(true)}>
              Clôturer la période
            </button>
          )}
          <BoutonExcel libelle="Exporter la synthèse du mois (Excel)" onClick={exporter} />
        </span>
      </div>
      {reouverture && <Erreur message={reouverture} />}

      {envoiFiches && (
        <EnvoiFichesPaie
          anneeId={donnees.annee_id}
          mois={moisChoisi}
          contratId={envoiFiches.contratId}
          onFermer={() => setEnvoiFiches(null)}
        />
      )}

      {cloture && (
        <ModaleCloture
          anneeId={donnees.annee_id}
          mois={moisChoisi}
          salaries={lignes.filter((l) => l.net !== 0).length}
          total={totaux.net}
          ecartes={donnees.creneaux_ecartes}
          onFermer={() => setCloture(false)}
          onCloture={() => {
            setCloture(false);
            onChange();
          }}
        />
      )}

      <div className="carte overflow-x-auto lg:overflow-x-visible">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-encre-500">
              <th className={`${ENTETE_FIGEE} px-3 py-2`} style={HAUT_FIGE}>
                Salarié
              </th>
              <th className={`${ENTETE_FIGEE} px-3 py-2 text-right`} style={HAUT_FIGE}>
                <Infobulle texte="Moyenne lissée sur l’année, pas les heures réellement faites ce mois-ci">
                  Durée&nbsp;*
                </Infobulle>
              </th>
              <th className={`${ENTETE_FIGEE} px-3 py-2 text-center`} style={HAUT_FIGE}>
                <Infobulle texte="Le contrat est éligible à l’assiette forfaitaire de cotisations (professeurs et animateurs, case de la fiche de contrat).">
                  Base forfaitaire
                </Infobulle>
              </th>
              <th className={`${ENTETE_FIGEE} px-3 py-2 text-right`} style={HAUT_FIGE}>
                Salaire&nbsp;*
              </th>
              <th className={`${ENTETE_FIGEE} px-3 py-2 text-right`} style={HAUT_FIGE}>
                Prime anc.
              </th>
              <th className={`${ENTETE_FIGEE} px-3 py-2 text-right`} style={HAUT_FIGE}>
                Transport
              </th>
              <th className={`${ENTETE_FIGEE} px-3 py-2 text-right`} style={HAUT_FIGE}>
                Total
              </th>
              {envoiPossible && (
                <th className={`${ENTETE_FIGEE} w-8 px-1 py-2`} style={HAUT_FIGE}>
                  <span className="sr-only">Envoyer la fiche de paie</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {lignes.map((l) => (
              // La ligne ouvre le detail du salarie sur CE mois, comme
              // dans le tableau annuel (2026-10-02).
              <tr
                key={l.personnel.contrat_id}
                className={`cursor-pointer border-b border-encre-100 hover:bg-encre-50 ${
                  l.net === 0 ? 'text-encre-300' : ''
                }`}
                onClick={() => onDetail(l.personnel.contrat_id, moisChoisi)}
              >
                <td className="whitespace-nowrap px-3 py-1.5 font-medium">
                  {l.personnel.prenom_usuel}{' '}
                  <span className="uppercase text-encre-500">{l.personnel.nom ?? ''}</span>
                  <IconesAvantages personnel={l.personnel} />
                </td>
                {/* Heures du mois, mensualisees comme le salaire : les
                    deux colonnes se divisent pour retrouver un taux
                    horaire. Une base ponctuelle compte ses heures en
                    entier sur son seul mois. */}
                <td className="tabulaire px-3 py-1.5 text-right">{heures(l.detail.heures)}</td>
                <td className="px-3 py-1.5 text-center">
                  {baseForfaitaire(l.personnel.base_forfaitaire)}
                </td>
                {/* Salaire NET DE MUTUELLE : la cotisation du mois est
                    deja retranchee — d'ou le tag, qui explique l'ecart
                    avec la base seule, et l'infobulle qui pose le calcul.
                    Elle s'ouvre VERS LE HAUT : le tableau defile dans un
                    conteneur qui rogne ce qui deborde par le bas, et la
                    derniere ligne y perdrait sa bulle. */}
                <td className="tabulaire px-3 py-1.5 text-right">
                  <MontantNature
                    nature={l.personnel.statut === 'administratif' ? l.personnel.natures_salaire[index] : null}
                  >
                  {l.detail.mutuelle > 0 ? (
                    <MontantExplique
                      texte={`${formaterMontant(l.detail.salaire)} − ${formaterMontant(l.detail.mutuelle)} de mutuelle = ${formaterMontant(l.salaireNet)}`}
                      accessible={`Salaire ${formaterMontant(l.detail.salaire)} moins mutuelle ${formaterMontant(l.detail.mutuelle)}, soit ${formaterMontant(l.salaireNet)}`}
                    >
                      {montant(l.salaireNet, true)}
                    </MontantExplique>
                  ) : (
                    montant(l.salaireNet, true)
                  )}
                  </MontantNature>
                </td>
                <td className="tabulaire px-3 py-1.5 text-right">{montant(l.detail.prime, true)}</td>
                {/* Un transport rembourse SANS JUSTIFICATIF se souligne en
                    pointille, avec le motif en infobulle (Georges,
                    2026-11-04) : la synthese est le moment ou l'on
                    reclame le titre avant de declarer. Meme bulle que la
                    mutuelle. */}
                <td className="tabulaire px-3 py-1.5 text-right">
                  {l.detail.transport > 0 && l.personnel.transports_mensuels[index]?.justificatif === false ? (
                    <MontantExplique texte="Justificatif de transport non fourni">
                      {montant(l.detail.transport, true)}
                    </MontantExplique>
                  ) : (
                    montant(l.detail.transport, true)
                  )}
                </td>
                <td className="tabulaire px-3 py-1.5 text-right font-medium">{montant(l.net)}</td>
                {envoiPossible && (
                  // La ligne ouvre le detail : l'enveloppe ne doit pas l'ouvrir au passage.
                  <td className="px-1 py-1.5 text-center" onClick={(evenement) => evenement.stopPropagation()}>
                    {l.net !== 0 && (
                      <Infobulle
                        texte={`Envoyer sa fiche de paie à ${l.personnel.prenom_usuel} ${(l.personnel.nom ?? '').toUpperCase()}`.trim()}
                        position="bas"
                        className="inline-flex align-middle"
                      >
                        <button
                          type="button"
                          className="rounded p-1 text-encre-400 hover:bg-encre-100 hover:text-encre-800"
                          aria-label={`Envoyer sa fiche de paie à ${l.personnel.prenom_usuel} ${l.personnel.nom ?? ''}`.trim()}
                          onClick={() => setEnvoiFiches({ contratId: l.personnel.contrat_id })}
                        >
                          <IconeEnveloppe />
                        </button>
                      </Infobulle>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-encre-300 font-medium">
              <td className="px-3 py-2">Total</td>
              <td className="tabulaire px-3 py-2 text-right">{heures(totaux.heures)}</td>
              <td className="px-3 py-2" />
              <td className="tabulaire px-3 py-2 text-right">
                {formaterMontant(totaux.salaireNet)}
              </td>
              <td className="tabulaire px-3 py-2 text-right">{formaterMontant(totaux.prime)}</td>
              <td className="tabulaire px-3 py-2 text-right">{formaterMontant(totaux.transport)}</td>
              <td className="tabulaire px-3 py-2 text-right">{formaterMontant(totaux.net)}</td>
              {envoiPossible && <td />}
            </tr>
          </tfoot>
        </table>
      </div>

      <LegendeNature classe="mt-2" />
      <p className="mt-1 text-xs text-encre-400">
        * <strong>Durée</strong> et <strong>salaire</strong> sont des moyennes{' '}
        <strong>lissées</strong> : ce qui reste dû sur l’année (séances × durée × taux, moins ce
        qui est déjà clôturé), réparti à parts égales sur les mois ouverts restants. Ce ne sont pas
        les heures réellement faites ce mois-là. <strong>Base forfaitaire</strong> dit si le contrat
        est éligible à l’assiette forfaitaire de cotisations ; la case se règle sur la fiche de
        contrat, pour un professeur ou un animateur. Un <strong>transport</strong>{' '}
        <span className="border-b border-dotted border-encre-300">souligné en pointillé</span> attend le
        justificatif du titre.
      </p>

      {/* Les creneaux que le calcul ecarte, avec leur motif : c'est ce
          que la generation montrait avant d'ecrire. Ils ne disparaissent
          pas en silence — un professeur sans contrat n'est pas paye, il
          faut le voir avant de cloturer. */}
      {/* Une variable de paie manque sur un mois ouvert : la mutuelle ou la
          prime y est comptee a zero, ce qu'on doit savoir avant de cloturer. */}
      {!estCloture && donnees.alertes.length > 0 && (
        <div className="mt-3 rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-800">
          <p className="font-medium">Variables de paie manquantes (fiche de l’école, Administration → Entités)</p>
          <ul className="mt-1 space-y-0.5">
            {donnees.alertes.map((a) => (
              <li key={a}>{a}</li>
            ))}
          </ul>
        </div>
      )}

      {!estCloture && donnees.creneaux_ecartes.length > 0 && (
        <details className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs">
          <summary className="cursor-pointer font-medium text-amber-900">
            {donnees.creneaux_ecartes.length} créneau(x) écarté(s) du calcul
          </summary>
          <ul className="mt-2 space-y-0.5 text-amber-800">
            {donnees.creneaux_ecartes.map((c) => (
              <li key={c.creneau_id}>
                {c.activite} · {libelleJour(c.jour)} {formaterHeure(c.heure_debut)}
                {c.personnel ? ` · ${c.personnel}` : ''} — {c.motif}
              </li>
            ))}
          </ul>
        </details>
      )}
    </>
  );
}

/**
 * Les elements de paie d'un personnel — le detail qui fait le net. Chaque
 * ligne montre le creneau qu'elle paie, la periode reellement enseignee
 * (saisie, ou par defaut le calendrier des seances), et ce qu'elle vaut.
 *
 * Modifier ou supprimer une ligne existante reecrit des salaires deja
 * verses : ces gestes sont reserves a `paie.corriger` (super admin). La
 * Direction corrige en AJOUTANT un element — negatif au besoin, comme le
 * faisait le classeur.
 */
function DetailPersonnel({
  personnel,
  mode,
  moisInitial,
  sansAjout,
  anneeId,
  mois,
  moisClotures,
  peutGerer,
  peutCorriger,
  onFermer,
  onChange,
}: {
  personnel: PaiePersonnel;
  /**
   * Le REGIME de l'onglet d'ou le detail est ouvert (2026-09-30). Une
   * meme personne peut relever des deux — un cours au regime general,
   * une chorale au cachet : le detail ne montre que les elements du
   * regime regarde, sans quoi les deux onglets affichaient la meme liste
   * melangee sous deux totaux differents.
   */
  mode: 'general' | 'cachet';
  /** Le mois sur lequel ouvrir, quand on vient de la synthese d'un mois. */
  moisInitial?: string | null;
  /** Consultation seule : pas de « Nouvel element ». */
  sansAjout?: boolean;
  anneeId: string;
  mois: string[];
  moisClotures: string[];
  peutGerer: boolean;
  peutCorriger: boolean;
  onFermer: () => void;
  onChange: () => void;
}) {
  const [edition, setEdition] = useState<BaseCalculee | null>(null);
  const [creation, setCreation] = useState(false);
  const clotures = new Set(moisClotures);
  // Un element exceptionnel ne se saisit que sur un mois OUVERT : un
  // mois declare ne recoit plus rien.
  const moisOuverts = mois.filter((m) => !clotures.has(m));

  // Les creneaux que ce personnel encadre cette annee : le selecteur du
  const supprimer = async (base: BaseCalculee) => {
    if (!window.confirm('Supprimer cet élément de paie ?')) return;
    await api.delete(`/api/paie/bases/${base.id}`);
    onChange();
  };

  // Les mois REELLEMENT portes par des elements, calcules ou enregistres :
  // proposer un mois vide ferait chercher une ligne qui n'existe pas. On
  // ouvre sur le premier mois OUVERT — celui qu'on s'apprete a declarer.
  //
  // Le partage des regimes vit dans lib/paie.ts, le tableau dans
  // components/ElementsPaie.tsx.
  const moisPortes = moisPortesDe(personnel, mode);
  // Ouvert depuis la SYNTHESE d'un mois, le detail reste sur ce mois —
  // meme si le salarie n'y porte aucun element : montrer un autre mois
  // sous le titre de celui-ci serait faux (2026-10-02).
  const moisImpose = moisInitial ?? null;
  const [moisFiltre, setMoisFiltre] = useState(
    moisImpose ??
      moisPortes.find((m) => !clotures.has(m)) ??
      moisPortes[moisPortes.length - 1] ??
      '',
  );
  return (
    <Modale
      titre={
        <>
          Élément de paie pour {personnel.prenom_usuel} {personnel.nom ?? ''}{' '}
          <Badge>{libelleStatutContrat(personnel.statut)}</Badge>{' '}
          {/* Le temps de travail ne se dit que s'il fait un salaire fixe. */}
          {natureRemuneration(personnel.statut, personnel.temps_travail) !== 'reel' && (
            <>
              <Badge>{libelleTempsTravail(personnel.temps_travail)}</Badge>{' '}
            </>
          )}
          <Badge ton="contour">{mode === 'cachet' ? 'Cachet' : 'Régime général'}</Badge>
        </>
      }
      titreAccessible={`Élément de paie pour ${personnel.prenom_usuel} ${personnel.nom ?? ''}`}
      ouverte
      onFermer={onFermer}
      large
    >
      <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-encre-600">
        <ResumePaie personnel={personnel} mode={mode} mois={mois} moisFiltre={moisFiltre} />
        {/* Depuis la synthese d'un mois, le mois est une INFORMATION : on
            regarde ce mois-la, on n'en change pas ici. */}
        {moisImpose && (
          <span className="ml-auto text-xs text-encre-500">
            Mois : <span className="font-medium text-encre-800">{formaterMois(moisImpose)}</span>
          </span>
        )}
        {!moisImpose && moisPortes.length > 0 && (
          <span className="ml-auto flex items-center gap-2">
            {/* Un salarie porte jusqu'a 20 creneaux, soit 240 lignes sur
                l'annee : le mois n'est plus une colonne, c'est un filtre. */}
            <label className="text-xs text-encre-500" htmlFor="mois-element">
              Mois
            </label>
            <select
              id="mois-element"
              // Navigation, pas saisie : la modale ne doit pas le compter
              // comme une modification a confirmer.
              data-navigation
              className="champ py-1 text-sm"
              value={moisFiltre}
              onChange={(e) => setMoisFiltre(e.target.value)}
            >
              {moisPortes.map((m) => (
                <option key={m} value={m}>
                  {clotures.has(m) ? '🔒 ' : ''}
                  {formaterMois(m)}
                </option>
              ))}
            </select>
          </span>
        )}
      </div>

      <TableauElementsPaie
        personnel={personnel}
        mode={mode}
        moisFiltre={moisFiltre}
        moisClotures={moisClotures}
        peutCorriger={peutCorriger}
        onModifier={setEdition}
        onSupprimer={(base) => void supprimer(base)}
      />

      {/* Un element exceptionnel se saisit depuis CHAQUE onglet (2026-10-07)
          et part dans le regime de l'onglet : depuis le cachet, il porte le
          drapeau `cachet` — c'est ce qui garde la trace d'un remplacement.
          Sans creneau, rien d'autre ne pourrait le classer. */}
      {peutGerer && !sansAjout && !creation && !edition && moisOuverts.length > 0 && (
        <button type="button" className="bouton-secondaire mt-3" onClick={() => setCreation(true)}>
          Nouvel élément
        </button>
      )}

      {(creation || edition) && (
        <FormulaireBase
          contratId={personnel.contrat_id}
          anneeId={anneeId}
          mois={moisOuverts}
          base={edition}
          cachet={mode === 'cachet'}
          onFermer={() => {
            setCreation(false);
            setEdition(null);
          }}
          onEnregistre={() => {
            setCreation(false);
            setEdition(null);
            onChange();
          }}
        />
      )}
    </Modale>
  );
}

/**
 * Un element EXCEPTIONNEL : remplacement, prestation, regularisation.
 *
 * Il n'a PAS de creneau, et c'est structurant : le triptyque (contrat,
 * creneau, mois) reste donc incomplet, la generation ne le touche jamais,
 * et il reste corrigeable. C'est le pendant du verrou pose sur les lignes
 * generees.
 */
function FormulaireBase({
  contratId,
  anneeId,
  mois,
  base,
  cachet = false,
  onFermer,
  onEnregistre,
}: {
  contratId: string;
  anneeId: string;
  mois: string[];
  base: BaseCalculee | null;
  /** Ouvert depuis l'onglet Cachet : l'element en releve. */
  cachet?: boolean;
  onFermer: () => void;
  onEnregistre: () => void;
}) {
  const [valeurs, setValeurs] = useState({
    libelle: '',
    recurrent: false,
    mois: mois[0] ?? '',
    moisFin: mois[1] ?? mois[0] ?? '',
    montant: '',
    minutes: '',
  });
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [initialisePour, setInitialisePour] = useState<string | null>(null);

  const cle = base?.id ?? 'nouvelle';
  if (cle !== initialisePour) {
    setInitialisePour(cle);
    setValeurs({
      libelle: base?.libelle ?? '',
      recurrent: false,
      mois: base?.mois ?? mois[0] ?? '',
      moisFin: base?.mois ?? mois[0] ?? '',
      montant: base ? base.montant.toFixed(2) : '',
      // La duree se saisit en MINUTES — comme un creneau — et se stocke
      // en heures decimales, comme tout le module.
      minutes: base ? String(Math.round(base.heures * 60)) : '',
    });
    setErreur(null);
  }

  // UN ELEMENT EXCEPTIONNEL VAUT UN MOIS par defaut. « Recurrent »
  // ouvre une date de fin, et cree autant de lignes que de mois couverts
  // — chacune portant le meme montant.
  const indexDebut = mois.indexOf(valeurs.mois);
  const indexFin = mois.indexOf(valeurs.moisFin);
  const nbMois =
    valeurs.recurrent && indexDebut >= 0 && indexFin > indexDebut
      ? indexFin - indexDebut + 1
      : 1;

  const nombre = (texte: string) => Number(texte.replace(',', '.')) || 0;
  const totalVerse = Math.round(nombre(valeurs.montant) * nbMois * 100);

  const soumettre = async (evenement: FormEvent) => {
    evenement.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      const corps = {
        // Jamais de creneau : voir la note du composant.
        creneauId: null,
        libelle: valeurs.libelle || null,
        mois: valeurs.mois,
        // La recurrence est DEPLIEE PAR LE SERVEUR : c'est lui qui sait
        // combien de mois l'exercice porte, et la regle n'a pas a vivre
        // en double.
        moisFin: nbMois > 1 ? valeurs.moisFin : null,
        montant: nombre(valeurs.montant),
        heures: nombre(valeurs.minutes) / 60,
        // Un element exceptionnel n'a pas de taux : il porte un montant,
        // pas un volume x un prix.
        tauxHoraire: 0,
        cachet,
      };
      if (base) {
        await api.patch(`/api/paie/bases/${base.id}`, { ...corps, rang: base.rang });
      } else {
        await api.post('/api/paie/bases', { ...corps, contratId, anneeId });
      }
      onEnregistre();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Enregistrement impossible.');
    } finally {
      setEnCours(false);
    }
  };

  const libelleMoisLong = (iso: string) => formaterMois(iso);

  return (
    <form onSubmit={soumettre} className="mt-3 space-y-3 rounded-md border border-encre-200 p-3">
      {/* MOIS et LIBELLE sur une ligne, DUREE et MONTANT sur la suivante.
          Plus de creneau ni de dates de cours : un element exceptionnel
          n'en a pas. */}
      <div className="grid grid-cols-2 gap-3">
        <Champ libelle="Mois">
          <select
            className="champ"
            value={valeurs.mois}
            onChange={(e) =>
              setValeurs({
                ...valeurs,
                mois: e.target.value,
                // La fin est STRICTEMENT posterieure : une recurrence d'un
                // seul mois n'est pas une recurrence.
                moisFin:
                  valeurs.moisFin > e.target.value
                    ? valeurs.moisFin
                    : (mois[mois.indexOf(e.target.value) + 1] ?? e.target.value),
              })
            }
          >
            {mois.map((m) => (
              <option key={m} value={m}>
                {libelleMoisLong(m)}
              </option>
            ))}
          </select>
        </Champ>
        <Champ libelle="Libellé" aide="Ce que paie l’élément : un remplacement, une prestation…">
          <input
            className="champ"
            value={valeurs.libelle}
            onChange={(e) => setValeurs({ ...valeurs, libelle: e.target.value })}
          />
        </Champ>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Champ libelle="Durée (min)">
          <input
            className="champ tabulaire"
            inputMode="numeric"
            value={valeurs.minutes}
            onChange={(e) => setValeurs({ ...valeurs, minutes: e.target.value })}
          />
        </Champ>
        <Champ libelle="Salaire mensuel (€)" aide="Négatif pour une régularisation.">
          <input
            className="champ tabulaire"
            inputMode="decimal"
            value={valeurs.montant}
            onChange={(e) => setValeurs({ ...valeurs, montant: e.target.value })}
            required
          />
        </Champ>
      </div>

      {/* La recurrence et sa borne vont ensemble : la case sans la date
          laisse la question en suspens. */}
      <div className="grid grid-cols-2 items-start gap-3">
        <label className="inline-flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={valeurs.recurrent}
            onChange={(e) =>
              setValeurs({
                ...valeurs,
                recurrent: e.target.checked,
                // La fin est dans le FUTUR : au minimum le mois suivant.
                moisFin:
                  valeurs.moisFin > valeurs.mois
                    ? valeurs.moisFin
                    : (mois[mois.indexOf(valeurs.mois) + 1] ?? valeurs.mois),
              })
            }
          />
          <span>Récurrent</span>
        </label>

        {valeurs.recurrent && (
          <Champ libelle="Date de fin">
            <select
              className="champ"
              value={valeurs.moisFin}
              onChange={(e) => setValeurs({ ...valeurs, moisFin: e.target.value })}
            >
              {mois
                .filter((m) => m > valeurs.mois)
                .map((m) => (
                  <option key={m} value={m}>
                    {libelleMoisLong(m)}
                  </option>
                ))}
            </select>
          </Champ>
        )}
      </div>

      <p className="text-sm text-encre-600">
        {nbMois > 1 ? (
          <>
            {nbMois} lignes de{' '}
            <span className="tabulaire">
              {formaterMontant(Math.round(nombre(valeurs.montant) * 100))}
            </span>
            , soit <span className="tabulaire font-medium">{formaterMontant(totalVerse)}</span> au
            total.
          </>
        ) : (
          <>
            ={' '}
            <span className="tabulaire font-medium">
              {formaterMontant(Math.round(nombre(valeurs.montant) * 100))}
            </span>{' '}
            net, sur le seul mois choisi.
          </>
        )}
      </p>

      <PiedFormulaire
        erreur={erreur}
        enCours={enCours}
        onFermer={onFermer}
        libelle={base ? 'Enregistrer' : 'Créer l’élément'}
      />
    </form>
  );
}

/** Le cadenas d'un mois cloture — un glyphe, pas une image. */
function Cadenas() {
  return (
    <svg
      viewBox="0 0 16 16"
      className="mr-1 inline-block h-3 w-3 align-[-1px]"
      fill="currentColor"
      aria-hidden
    >
      <path d="M4 7V5a4 4 0 1 1 8 0v2h1a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1h1zm1.5 0h5V5a2.5 2.5 0 0 0-5 0v2z" />
    </svg>
  );
}

/**
 * CLOTURE D'UNE PERIODE DE PAIE — on montre avant d'enregistrer.
 *
 * C'est le seul geste d'ecriture de la paie des creneaux (2026-09-08) :
 * les mois ouverts se calculent, la cloture enregistre le mois tel qu'il
 * est calcule et le rend intouchable. Le bouton dit ce qu'il va figer —
 * combien de salaries, quel total, et les creneaux ecartes avec leur
 * motif — parce qu'apres, la declaration CEA est faite sur ces chiffres.
 */
function ModaleCloture({
  anneeId,
  mois,
  salaries,
  total,
  ecartes,
  onFermer,
  onCloture,
}: {
  anneeId: string;
  mois: string;
  salaries: number;
  /** Net du mois, regime general, en centimes. */
  total: number;
  ecartes: CalculPaie['creneaux_ecartes'];
  onFermer: () => void;
  onCloture: () => void;
}) {
  const [reference, setReference] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const cloturer = async (evenement: FormEvent) => {
    evenement.preventDefault();
    setErreur(null);
    setEnCours(true);
    try {
      await api.post<BilanCloture>('/api/paie/periodes', {
        anneeId,
        mois,
        reference: reference.trim() || null,
      });
      onCloture();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'Clôture impossible.');
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Modale titre={`Clôturer la paie de ${formaterMois(mois)}`} ouverte onFermer={onFermer}>
      <form onSubmit={cloturer} className="space-y-4">
        <div className="rounded-md border border-encre-200 bg-encre-50 p-3 text-sm">
          <p>
            <strong className="tabulaire">{salaries}</strong> salarié(s) au régime général, pour un
            net de <strong className="tabulaire">{formaterMontant(total)}</strong>.
          </p>
          <p className="mt-2 text-encre-600">
            Pour chaque salarié, ce qui est déclaré sera enregistré tel qu’il est affiché et ne
            bougera plus : salaire, prime d’ancienneté, heures, transport et mutuelle. Le détail par
            cours n’est pas conservé. Toute modification d’inscription qui changerait ce mois sera
            refusée ; la réouverture est réservée au super administrateur.
          </p>
        </div>

        {ecartes.length > 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs">
            <p className="font-medium text-amber-900">
              {ecartes.length} créneau(x) écarté(s) — ils ne seront pas payés ce mois
            </p>
            <ul className="mt-1 max-h-32 space-y-0.5 overflow-y-auto text-amber-800">
              {ecartes.map((c) => (
                <li key={c.creneau_id}>
                  {c.activite} · {libelleJour(c.jour)} {formaterHeure(c.heure_debut)}
                  {c.personnel ? ` · ${c.personnel}` : ''} — {c.motif}
                </li>
              ))}
            </ul>
          </div>
        )}

        <Champ libelle="Référence de la déclaration" aide="Facultatif : le numéro CEA, une date…">
          <input
            className="champ"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            maxLength={120}
          />
        </Champ>

        <PiedFormulaire
          erreur={erreur}
          enCours={enCours}
          onFermer={onFermer}
          libelle="Clôturer la période"
        />
      </form>
    </Modale>
  );
}
